import { useMemo, type ReactNode } from 'react';
import type { FieldDef, FormSettings, FormType, PublicForm } from '@jfh/intake-shared';
import { ErrorSummary, Review, StepFields, StepHeader, useApplicantForm } from '@jfh/intake-forms/renderer';
import { api } from '../../lib/api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { StepBar } from './parts.js';
import { stepsOf } from './logic.js';
import { useFormPreviewData } from './useFormPreviewData.js';

export interface TryItOutProps {
  fields: FieldDef[];
  formName: string;
  formType: FormType;
  slug: string;
  settings: FormSettings;
  onStop: () => void;
  /** Wraps the right-hand panel (the builder places it). */
  panel: (content: ReactNode) => ReactNode;
}

/**
 * Try it out (F9 P3): the draft answered as an applicant would, with the
 * public form's own steps, rules, validation and Companies House lookup (the
 * real one, through the admin's /api proxy). Nothing is saved or sent.
 */
export function TryItOut(props: TryItOutProps) {
  const { resolved, countryOptions, countryConfig } = useFormPreviewData(props.fields);
  const form = useMemo<PublicForm>(
    () => ({
      slug: props.slug,
      name: props.formName,
      type: props.formType,
      version: 0,
      fields: resolved.filter((f) => f.audience !== 'staff'),
      turnstile: false,
      success: props.settings.success,
      disabledCountries: countryConfig.disabled,
      defaultCountry: countryConfig.default,
      ...(countryOptions.length ? { countryOptions } : {}),
    }),
    [props.slug, props.formName, props.formType, props.settings.success, resolved, countryConfig, countryOptions],
  );
  return <TryFlow {...props} form={form} />;
}

function TryFlow({ form, fields, onStop, panel }: TryItOutProps & { form: PublicForm }) {
  const toast = useToast();
  const flow = useApplicantForm(form, { lookupCompany: api.lookupCompany });
  const steps = stepsOf(fields);
  // Only the steps applicants would see now (as the form's own progress bar):
  // a step whose questions are all hidden by rules, or skipped, drops out.
  const shown = steps
    .map((step, i) => ({ step, i }))
    .filter(({ i }) => flow.pages[i] && flow.pageActive(flow.pages[i]!));
  const page = flow.pages[flow.step];
  const lastPage = flow.step === flow.reviewIndex - 1;

  const submit = () => {
    if (!flow.checkAll()) {
      toast({ title: 'Some answers need fixing', body: 'The first step with a problem is open.', icon: 'flag' });
      return;
    }
    toast({
      title: 'Test submission checked',
      body: 'Everything required was answered. Nothing was sent or saved.',
      icon: 'check',
    });
  };

  const hasCompany = form.fields.some((f) => f.type === 'company_number');
  const checks = [
    ...(hasCompany
      ? [
          'Enter a company number to see the Companies House look-up fill in and lock the company’s details.',
          'Change an answer that a rule depends on, and watch questions appear and disappear.',
        ]
      : ['Change an answer that a rule depends on, and watch questions appear and disappear.']),
    'Press Continue without answering to see the “needs an answer” messages.',
    'Staff-only questions are never shown to applicants.',
  ];

  return (
    <>
      <div className="scroll-y" style={{ background: 'var(--jfh-sea-shell)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '18px 40px 80px' }}>
          <div
            role="status"
            style={{
              fontSize: 12.5,
              color: 'var(--accent-text)',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontWeight: 700,
              background: 'var(--surface-brand-tint)',
              border: '1px solid var(--border-brand-soft)',
              borderRadius: 'var(--r-md)',
              padding: '7px 10px',
              marginBottom: 12,
            }}
          >
            <Icon name="send" size={14} style={{ flex: 'none' }} /> Trying it out: answer as an applicant. Show/hide rules
            and the Companies House look-up work as they will when live. Nothing is saved or sent.
          </div>

          <div style={{ marginBottom: 14 }}>
            <StepBar
              steps={shown.map((x) => x.step)}
              current={flow.isReview ? null : shown.findIndex((x) => x.i === flow.step)}
              onPick={(j) => {
                flow.setErrors({});
                flow.setStep(shown[j]!.i);
              }}
            />
          </div>

          <form
            key={flow.step}
            className="card rdr-form-card fade-in"
            style={{ padding: 28 }}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (flow.isReview) submit();
              else flow.goNext();
            }}
          >
            <StepHeader flow={flow} />
            <ErrorSummary flow={flow} />
            {flow.isReview ? (
              <Review
                pages={flow.pages}
                values={flow.visValues}
                visible={flow.vis}
                onEdit={(i) => flow.setStep(i)}
                turnstileEnabled={false}
                turnstileChecked={false}
                onToggleTurnstile={() => undefined}
              />
            ) : (
              <StepFields flow={flow} fields={page?.fields ?? []} />
            )}
            <div className="rdr-nav">
              {(flow.isReview ? flow.activeCount > 0 : flow.hasPrevSection) && (
                <button type="button" className="btn lg" onClick={flow.goBack}>
                  <Icon name="arrowLeft" size={16} /> Back
                </button>
              )}
              <button type="submit" className="btn primary lg rdr-grow">
                {flow.isReview ? (
                  <>
                    <Icon name="send" size={16} /> Send application (test only)
                  </>
                ) : (
                  <>
                    {lastPage ? 'Check your answers' : 'Continue'} <Icon name="chevronR" size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {panel(
        <div style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 10,
            }}
          >
            Trying it out
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--text-2)' }}>
            Answer the questions as an applicant would. This is your draft, so changes you haven’t published are
            included. Questions appear and disappear as their rules say, and the company number look-up checks
            Companies House for real.
          </p>
          <div className="card" style={{ padding: '14px 16px', display: 'grid', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>Things to check</div>
            {checks.map((c) => (
              <div key={c} style={{ display: 'flex', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                <span style={{ color: 'var(--jfh-fern-green)', marginTop: 2 }}>
                  <Icon name="check" size={13} />
                </span>
                <span>{c}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button type="button" className="btn sm" onClick={() => flow.reset()}>
              Clear my answers
            </button>
            <button type="button" className="btn primary sm" onClick={onStop}>
              <Icon name="pencil" size={14} /> Back to editing
            </button>
          </div>
        </div>,
      )}
    </>
  );
}
